import { Audio } from "@apis:audio/audio";
import { alpha, Box, Typography, useTheme } from "@mui/material";
import { speakerColor } from "@pages:app/audio/const";
import {
  formatClock,
  TranscriptTurn,
  wordsOf,
  wordText,
} from "@pages:app/audio/util";
import { KeyboardEvent, memo, MouseEvent } from "react";
import { useTranslation } from "react-i18next";

interface SpeakerTurnProps {
  turn: TranscriptTurn;
  number?: number;
  active?: { line: number; word: number | null } | null;
  onSeek?: (seconds: number) => void;
  enterDelayMs?: number | null;
}

const startOf = (event: MouseEvent<HTMLElement>): number | undefined => {
  const target = event.target as HTMLElement;
  const raw = target.closest<HTMLElement>("[data-start]")?.dataset.start;
  const start = raw !== undefined ? Number(raw) : NaN;
  return Number.isFinite(start) ? start : undefined;
};

/*
 * One turn of the conversation: the speaker's avatar and a colored bubble,
 * with the time range, and one line per segment.
 * While the recording plays, the spoken word lights up, or the line if there is no alignment.
 */
export const SpeakerTurn = memo(function SpeakerTurn({
  turn,
  number,
  active = null,
  onSeek,
  enterDelayMs = null,
}: SpeakerTurnProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const color = number !== undefined ? speakerColor(number - 1) : undefined;
  const accent = color ?? theme.palette.primary.main;
  const tint = color ? alpha(color, 0.06) : theme.palette.grey[50];
  const highlight = alpha(accent, 0.22);
  const range = `${formatClock(turn.start)} – ${formatClock(turn.end)}`;

  const seekFrom = (
    event: MouseEvent<HTMLElement>,
    line: Audio["GET"]["Segment"],
  ) => {
    onSeek?.(startOf(event) ?? line.start);
  };

  const seekByKey = (
    event: KeyboardEvent<HTMLElement>,
    line: Audio["GET"]["Segment"],
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSeek?.(line.start);
    }
  };

  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        listStyle: "none",
        animation:
          enterDelayMs === null
            ? "none"
            : `audio-turn-in 360ms ease-out ${enterDelayMs}ms backwards`,
        "@keyframes audio-turn-in": {
          from: { opacity: 0, transform: "translateY(8px)" },
          to: { opacity: 1, transform: "none" },
        },
      }}
    >
      {color && number !== undefined && (
        <Box
          aria-hidden
          sx={{
            flex: "0 0 auto",
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: color,
            color: theme.palette.common.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
          }}
        >
          {number}
        </Box>
      )}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          maxWidth: 680,
          bgcolor: tint,
          borderRadius: "20px",
          borderTopLeftRadius: color ? "8px" : "20px",
          px: 2.25,
          py: 1.75,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1.25,
            flexWrap: "wrap",
            mb: 0.5,
          }}
        >
          {color && number !== undefined && (
            <Typography
              component="span"
              sx={{ fontSize: 13.5, fontWeight: 700, color }}
            >
              {t("audio.speaker", { n: number })}
            </Typography>
          )}
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {range}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {turn.lines.map((line, lineIndex) => {
            const isActiveLine = active?.line === lineIndex;
            const activeWord = isActiveLine ? active?.word : null;
            const words = wordsOf(line);
            const hasWords = words.length > 0;

            return (
              <Typography
                key={lineIndex}
                component="p"
                role={onSeek ? "button" : undefined}
                tabIndex={onSeek ? 0 : undefined}
                aria-label={
                  onSeek
                    ? t("audio.transcript.playFrom", {
                        time: formatClock(line.start),
                      })
                    : undefined
                }
                onClick={onSeek ? (event) => seekFrom(event, line) : undefined}
                onKeyDown={
                  onSeek ? (event) => seekByKey(event, line) : undefined
                }
                sx={{
                  fontSize: 15,
                  lineHeight: 1.55,
                  overflowWrap: "anywhere",
                  borderRadius: "6px",
                  mx: -0.5,
                  px: 0.5,
                  cursor: onSeek ? "pointer" : "default",
                  bgcolor:
                    isActiveLine && !hasWords ? highlight : "transparent",
                  transition: "background-color 150ms ease-out",
                  "&:focus-visible": {
                    outline: `2px solid ${accent}`,
                    outlineOffset: 1,
                  },
                }}
              >
                {hasWords
                  ? words.map((word, wordIndex) => (
                      <Box
                        component="span"
                        key={wordIndex}
                        data-start={
                          Number.isFinite(word.start) ? word.start : undefined
                        }
                        sx={{
                          borderRadius: "4px",
                          px: "2px",
                          mx: "-2px",
                          bgcolor:
                            activeWord === wordIndex
                              ? highlight
                              : "transparent",
                          transition: "background-color 120ms ease-out",
                        }}
                      >
                        {wordText(word)}
                        {wordIndex < words.length - 1 ? " " : ""}
                      </Box>
                    ))
                  : line.text}
              </Typography>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});
